import { useEffect, useState } from "react";
import {
  TimePickerMenuPosition,
  TimePickerSize,
} from "./timePicker.types";

const COMPACT_MENU_MIN_WIDTH_PX = 240;
const VIEWPORT_MARGIN_PX = 8;

export interface UseTimePickerMenuPositionProps {
  isOpen: boolean;
  size: TimePickerSize;
  triggerRef: React.RefObject<HTMLDivElement>;
}

/** Calculates and tracks the dropdown menu position relative to the trigger. */
export const useTimePickerMenuPosition = (
  props: UseTimePickerMenuPositionProps,
): TimePickerMenuPosition | null => {
  const [menuPosition, setMenuPosition] = useState<
    TimePickerMenuPosition | null
  >(null);

  useEffect(() => {
    if (!props.isOpen) return;
    const updatePosition = (): void => {
      const rect = props.triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const minWidth =
        props.size === "compact" ? COMPACT_MENU_MIN_WIDTH_PX : rect.width;
      const width = Math.max(rect.width, minWidth);
      const viewportWidth = window.innerWidth;
      const maxLeft = Math.max(
        VIEWPORT_MARGIN_PX,
        viewportWidth - width - VIEWPORT_MARGIN_PX,
      );
      const preferredLeft =
        props.size === "compact" ? rect.right - width : rect.left;
      const left = Math.min(Math.max(preferredLeft, VIEWPORT_MARGIN_PX), maxLeft);

      setMenuPosition({
        top: rect.bottom + 4,
        left,
        width,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [props.isOpen, props.size, props.triggerRef]);

  return menuPosition;
};
